import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  BarChart3,
  Boxes,
  DollarSign,
  Mail,
  Package,
  Plus,
  ShoppingBag,
  Truck,
} from "lucide-react";
import { api, money, dateTime } from "@/lib/api";
import { Card, PageHeader, StatusBadge, Btn } from "@/components/admin/ui";

const Stat = ({ label, value, icon: Icon, to, testid }) => {
  const body = (
    <div
      className="admin-card p-5 hover:border-zinc-300 transition-colors h-full"
      data-testid={testid}
    >
      <div className="flex items-center justify-between text-zinc-500 text-[13px]">
        {label}
        <Icon size={16} />
      </div>
      <p className="text-2xl font-bold mt-2 text-zinc-950">{value}</p>
    </div>
  );
  return to ? <Link to={to}>{body}</Link> : body;
};

export default function AdminHome() {
  const [d, setD] = useState(null);
  const [settings, setSettings] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [visitorSignals, setVisitorSignals] = useState(null);

  const loadDashboard = () => {
    api
      .get("/admin/dashboard")
      .then((r) => setD(r.data))
      .catch(() => setD({ error: true }));
    api
      .get("/admin/settings")
      .then((r) => setSettings(r.data))
      .catch(() => setSettings({}));
    api
      .get("/admin/analytics", { params: { days: 7 } })
      .then((r) => setAnalytics(r.data))
      .catch(() => setAnalytics(null));
    api
      .get("/admin/analytics/visitors")
      .then((r) => setVisitorSignals(r.data))
      .catch(() => setVisitorSignals(null));
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  if (!d)
    return (
      <div className="py-20 text-center text-zinc-500">Loading dashboard…</div>
    );
  if (d.error)
    return (
      <div className="py-20 text-center text-zinc-500">
        Dashboard data is temporarily unavailable.
      </div>
    );
  return (
    <div className="admin-command" data-testid="admin-home-page">
      <PageHeader
        title={
          new Date().getHours() < 12
            ? "GOOD MORNING, DON SPICY"
            : new Date().getHours() < 18
              ? "GOOD AFTERNOON, DON SPICY"
              : "GOOD EVENING, DON SPICY"
        }
        subtitle="Your Empire. Your Vision. Your World."
      >
        <Link to="/admin/products/new">
          <Btn data-testid="home-add-product-button">
            <Plus size={16} /> Add product
          </Btn>
        </Link>
      </PageHeader>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat
          label="Today's sales"
          value={money(d.sales_today)}
          icon={DollarSign}
          to="/admin/analytics"
          testid="stat-sales-today"
        />
        <Stat
          label="Total revenue"
          value={money(d.total_revenue)}
          icon={BarChart3}
          to="/admin/analytics"
          testid="stat-total-revenue"
        />
        <Stat
          label="Total orders"
          value={d.orders_total}
          icon={ShoppingBag}
          to="/admin/orders"
          testid="stat-total-orders"
        />
        <Stat
          label="Pending fulfillment"
          value={d.to_ship}
          icon={Truck}
          to="/admin/orders?tab=to_ship"
          testid="stat-to-ship"
        />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-[.85fr_1.15fr] gap-4 mt-4">
        <div className="command-panel p-5" data-testid="dashboard-live-visitors">
          <p className="command-eyebrow">Live storefront signal</p>
          <p className="text-4xl font-black text-orange-300 mt-3">{visitorSignals?.active_visitors ?? "—"}</p>
          <p className="text-sm text-zinc-400 mt-1">Visitors seen in the last five minutes</p>
          <p
            className="text-sm text-zinc-300 mt-4"
            data-testid="dashboard-monthly-visitors"
          >
            {visitorSignals?.unique_visitors_30d ?? "—"} unique storefront visitors in 30 days
          </p>
          <p className="text-xs text-zinc-500 mt-2" data-testid="dashboard-visitor-source">
            First-party storefront events only; hosted-site analytics are measured separately.
          </p>
        </div>
        <div className="command-panel p-5" data-testid="dashboard-top-pages">
          <p className="command-eyebrow">Most viewed storefront pages</p>
          <div className="mt-3 space-y-2">{visitorSignals?.top_pages?.length ? visitorSignals.top_pages.map((page) => <div className="flex justify-between gap-3 text-sm" key={page.path}><span className="truncate text-zinc-300">{page.path}</span><strong>{page.views}</strong></div>) : <p className="text-sm text-zinc-400">No visitor activity recorded yet.</p>}</div>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4 mt-4">
        <div
          className="admin-card min-w-0 p-5 sm:p-6"
          data-testid="dashboard-sales-chart"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold">Sales · last 7 days</p>
              <p className="text-xs text-zinc-500 mt-1">
                Paid order revenue only
              </p>
            </div>
            <BarChart3 size={18} className="text-zinc-400" />
          </div>
          {analytics ? (
            <div className="admin-sales-bars mt-7">
              {analytics.by_day.map((day) => {
                const high = Math.max(
                  ...analytics.by_day.map((entry) => entry.sales),
                  1,
                );
                return (
                  <div key={day.date} className="admin-sales-bar">
                    <span
                      style={{
                        height: `${Math.max(4, (day.sales / high) * 100)}%`,
                      }}
                      title={`${day.date}: ${money(day.sales)}`}
                    />
                    <small>{day.date.slice(5)}</small>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-zinc-500 mt-8">
              Loading verified sales data…
            </p>
          )}
        </div>
      </div>
      <div
        className="admin-quick-actions mt-4"
        data-testid="dashboard-quick-actions"
      >
        <Link
          to="/admin/products/new"
          data-testid="dashboard-add-product-action"
        >
          <Btn>
            <Plus size={15} /> Add product
          </Btn>
        </Link>
        <Link to="/admin/orders" data-testid="dashboard-view-orders-action">
          <Btn variant="secondary">
            <ShoppingBag size={15} /> View orders
          </Btn>
        </Link>
        <Link
          to="/admin/products"
          data-testid="dashboard-update-inventory-action"
        >
          <Btn variant="secondary">
            <Boxes size={15} /> Update inventory
          </Btn>
        </Link>
        <Link
          to="/admin/settings/shipping"
          data-testid="dashboard-shipping-center-action"
        >
          <Btn variant="secondary">
            <Truck size={15} /> Shipping Center
          </Btn>
        </Link>
        <Link to="/admin/campaigns" data-testid="dashboard-campaigns-action"><Btn variant="secondary"><Mail size={15} /> Campaigns</Btn></Link>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-4 mt-4">
        <div className="min-w-0 space-y-4">
          <Card
            title="Recent orders"
            action={
              <Link
                to="/admin/orders"
                className="text-sm text-zinc-600 hover:text-zinc-950"
              >
                View all
              </Link>
            }
            testid="recent-orders-card"
          >
            {d.recent_orders.length === 0 ? (
              <p className="text-sm text-zinc-500">
                No orders yet. Paid orders will appear here.
              </p>
            ) : (
              <div className="divide-y divide-zinc-100">
                {d.recent_orders.map((o) => (
                  <Link
                    key={o.id}
                    to={`/admin/orders/${o.id}`}
                    className="flex flex-wrap sm:flex-nowrap items-center justify-between py-2.5 text-sm hover:bg-zinc-50 -mx-2 px-2 rounded"
                  >
                    <span className="font-medium">#{o.number}</span>
                    <span className="basis-full sm:basis-auto flex-1 sm:px-4 py-1 sm:py-0 truncate text-zinc-600">
                      {o.customer.name}
                    </span>
                    <span className="flex items-center gap-1.5 sm:ml-auto">
                      <StatusBadge status={o.fulfillment_status} />
                      <StatusBadge status={o.payment_status} />
                    </span>
                    <span className="w-24 text-right">{money(o.total)}</span>
                  </Link>
                ))}
              </div>
            )}
          </Card>
        </div>
        <div className="min-w-0 space-y-4">
          <Card
            title="Low stock"
            description={`Colors with ${settings?.low_stock_threshold ?? 3} or fewer left`}
            testid="low-stock-card"
          >
            {d.low_stock.length === 0 ? (
              <p className="text-sm text-zinc-500">
                Everything is well stocked.
              </p>
            ) : (
              <ul className="space-y-2">
                {d.low_stock.map((l) => (
                  <li key={`${l.product_id}${l.color}`}>
                    <Link
                      to={`/admin/products/${l.product_id}`}
                      className="flex items-center justify-between text-sm hover:bg-zinc-50 -mx-2 px-2 py-1 rounded"
                    >
                      <span className="flex items-center gap-2 truncate">
                        <AlertTriangle
                          size={14}
                          className={
                            l.stock === 0 ? "text-red-500" : "text-amber-500"
                          }
                        />
                        {l.name} · {l.color}
                      </span>
                      <span
                        className={
                          l.stock === 0
                            ? "text-red-600 font-medium"
                            : "text-zinc-700"
                        }
                      >
                        {l.stock === 0 ? "Sold out" : `${l.stock} left`}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Card>
          <Card
            title="Messages"
            description={
              d.unread_messages
                ? `${d.unread_messages} new`
                : "From your contact page"
            }
            testid="messages-card"
          >
            {d.messages.length === 0 ? (
              <p className="text-sm text-zinc-500">No messages yet.</p>
            ) : (
              <ul className="space-y-3">
                {d.messages.map((m) => (
                  <li key={m.id} className="text-sm">
                    <a
                      href={`mailto:${m.email}`}
                      className="flex items-center gap-2 font-medium hover:underline"
                    >
                      <Mail size={13} />
                      {m.name}
                      {!m.read && (
                        <span className="w-1.5 h-1.5 rounded-full bg-[#e11d2a]" />
                      )}
                    </a>
                    <p className="text-zinc-600 line-clamp-2 mt-0.5">
                      {m.message}
                    </p>
                    <p className="text-xs text-zinc-400 mt-0.5">
                      {dateTime(m.created_at)}
                      {m.order_number && ` · Order ${m.order_number}`}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
